/**
 * BarModelTool.jsx — Cycle 55 · Teach & Explain
 * ──────────────────────────────────────────────
 * Singapore-style bar model with two primary modes:
 *   • compare  — two stacked bars side by side, one longer than the other
 *   • part-whole — one whole bar split into named parts
 * (Configurable, but defaults to compare per user's choice.)
 *
 * State: ?mode=compare&a=8&b=5&la=Anna&lb=Ben
 *        ?mode=part&parts=3,4,5&labels=red,green,blue&total=Total
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";

const BAR_COLORS = ["bg-red-300", "bg-emerald-300", "bg-sky-300", "bg-amber-300", "bg-fuchsia-300", "bg-lime-300"];

const clampNum = (v, def) => {
  const n = parseFloat(v ?? "");
  if (isNaN(n)) return def;
  return Math.max(0, n);
};

// Compare mode display.
function CompareBars({ a, b, la, lb }) {
  const max = Math.max(a, b, 1);
  const rowClass = "flex items-center gap-2";
  const Bar = ({ len, color, label, testid }) => (
    <div className="flex-1">
      <div className="relative h-14 rounded-xl border-[3px] border-slate-900 bg-slate-100">
        <div className={`h-full rounded-l-xl border-r-[3px] border-slate-900 ${color} flex items-center justify-end pr-2 shadow-inner`}
             style={{ width: `${(len / max) * 100}%` }} data-testid={testid}>
          <span className="font-heading text-sm font-black tabular-nums">{len}</span>
        </div>
      </div>
      <p className="mt-1 text-[11px] font-black uppercase tracking-widest text-slate-600 text-center">{label}</p>
    </div>
  );
  const diff = Math.abs(a - b);
  return (
    <div className="space-y-4">
      <div className={rowClass}><Bar len={a} color="bg-red-300"     label={la || "A"} testid="bm-bar-a" /></div>
      <div className={rowClass}><Bar len={b} color="bg-emerald-300" label={lb || "B"} testid="bm-bar-b" /></div>
      <div className="rounded-2xl border-[3px] border-slate-900 bg-yellow-100 p-3 text-center text-sm font-bold" data-testid="bm-diff">
        Difference: <span className="font-heading text-lg font-black tabular-nums">{diff}</span>
        <span className="mx-2 text-slate-400">·</span>
        Total: <span className="font-heading text-lg font-black tabular-nums">{a + b}</span>
      </div>
    </div>
  );
}

// Part-whole display.
function PartWholeBar({ parts, labels, totalLabel }) {
  const sum = parts.reduce((s, n) => s + n, 0) || 1;
  return (
    <div>
      <div className="relative flex h-16 rounded-2xl border-[3px] border-slate-900 overflow-hidden bg-slate-100">
        {parts.map((p, i) => (
          <div key={i}
               className={`${BAR_COLORS[i % BAR_COLORS.length]} border-r-[3px] border-slate-900 last:border-r-0 flex items-center justify-center`}
               style={{ width: `${(p / sum) * 100}%` }}
               data-testid={`bm-part-${i}`}>
            <span className="font-heading text-sm font-black tabular-nums">{p}</span>
          </div>
        ))}
      </div>
      <div className="mt-1 flex text-[10px] font-black uppercase tracking-widest text-slate-600">
        {parts.map((p, i) => (
          <div key={i} className="text-center px-1 truncate" style={{ width: `${(p / sum) * 100}%` }}>
            {labels[i] || `Part ${i + 1}`}
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-100 p-3 text-center text-sm font-bold" data-testid="bm-total">
        {totalLabel || "Total"}:
        <span className="ml-2 font-heading text-lg font-black tabular-nums">{sum}</span>
      </div>
    </div>
  );
}

export function BarModelDisplay({ config }) {
  const mode = config.mode === "part" ? "part" : "compare";
  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="bm-canvas">
      {mode === "compare"
        ? <CompareBars a={config.a} b={config.b} la={config.la} lb={config.lb} />
        : <PartWholeBar parts={config.parts} labels={config.labels} totalLabel={config.totalLabel} />}
    </div>
  );
}

export default function BarModelTool() {
  const [params, setParams] = useSearchParams();
  const mode = (params.get("mode") || "compare") === "part" ? "part" : "compare";
  const a  = clampNum(params.get("a"), 8);
  const b  = clampNum(params.get("b"), 5);
  const la = params.get("la") || "";
  const lb = params.get("lb") || "";
  const parts = (params.get("parts") || "3,4,5").split(",").map((v) => clampNum(v, 1));
  const labels = (params.get("labels") || "").split(",");
  const totalLabel = params.get("total") || "";

  const update = (patch) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === "") q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };

  const settingsPanel = (
    <div className="space-y-3">
      <div className="flex gap-2" data-testid="bm-mode-tabs">
        <button type="button" onClick={() => update({ mode: null })} data-testid="bm-tab-compare"
                className={`h-9 px-3 rounded-full border-2 border-slate-900 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] ${mode === "compare" ? "bg-yellow-300" : "bg-white"}`}>
          Compare
        </button>
        <button type="button" onClick={() => update({ mode: "part" })} data-testid="bm-tab-part"
                className={`h-9 px-3 rounded-full border-2 border-slate-900 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] ${mode === "part" ? "bg-yellow-300" : "bg-white"}`}>
          Part-whole
        </button>
      </div>
      {mode === "compare" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <label className="text-xs font-bold text-slate-700">A value
            <input type="number" step="1" value={a} onChange={(e) => update({ a: e.target.value })} data-testid="bm-a"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
          <label className="text-xs font-bold text-slate-700">A label
            <input type="text" value={la} onChange={(e) => update({ la: e.target.value })} data-testid="bm-la"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
          <label className="text-xs font-bold text-slate-700">B value
            <input type="number" step="1" value={b} onChange={(e) => update({ b: e.target.value })} data-testid="bm-b"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
          <label className="text-xs font-bold text-slate-700">B label
            <input type="text" value={lb} onChange={(e) => update({ lb: e.target.value })} data-testid="bm-lb"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-700 block">Parts (comma-separated numbers)
            <input type="text" value={parts.join(",")} onChange={(e) => update({ parts: e.target.value })} data-testid="bm-parts"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
          <label className="text-xs font-bold text-slate-700 block">Labels (comma-separated)
            <input type="text" value={labels.join(",")} onChange={(e) => update({ labels: e.target.value })} data-testid="bm-labels"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
          <label className="text-xs font-bold text-slate-700 block">Total label
            <input type="text" value={totalLabel} onChange={(e) => update({ total: e.target.value })} data-testid="bm-totallabel"
                   className="mt-1 h-9 w-full px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
          </label>
        </div>
      )}
    </div>
  );

  return (
    <ManipulativeFrame
      slug="bar-model"
      title="Bar Model"
      description="Draw compare bars or a part-whole bar. Ideal for word problems, differences and ratio."
      onReset={() => update({ mode: null, a: null, b: null, la: null, lb: null, parts: null, labels: null, total: null })}
      settingsPanel={settingsPanel}
      shareUrl={window.location.href}
    >
      <BarModelDisplay config={{ mode, a, b, la, lb, parts, labels, totalLabel }} />
    </ManipulativeFrame>
  );
}
